import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, FileText, Mail, Play, X, Check, Layers, ArrowRight, Send, Download, RotateCcw, ListChecks, Printer, Crosshair, Eye, CalendarDays } from 'lucide-react';
import { PrioBadge } from './PrioCriteriaPicker';
import PrintPreviewDialog from './PrintPreviewDialog';
import { fmtDur } from './BatchTimingSummary';
import { FlyerStockBlock } from './FlyerStockBlock';
import { FlyerQrPackages } from './FlyerQrPackages';

const POSTAL_BATCH = 50;
const fmtDay = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.` : '');

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `vor ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `vor ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h} h`;
  return `vor ${Math.round(h / 24)} Tagen`;
};

function PrintclubStatusPill({ job }) {
  const status = job.status;
  const step = job.step;
  const stepLabel = { building_pdf: 'PDF wird gebaut…', sending_email: 'E-Mail wird versendet…', sent: 'versendet', failed: 'fehlgeschlagen' }[step] || step;
  if (status === 'running') {
    return (
      <span data-testid="printclub-status-pill" className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5 w-fit">
        <Loader2 className="w-2.5 h-2.5 animate-spin" />
        Printclub: {stepLabel} · gestartet {relTime(job.started_at)} · Bestellung über {job.expected_flyers} Blanko-Flyer
      </span>
    );
  }
  if (status === 'sent') {
    return (
      <span data-testid="printclub-status-pill" className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5 w-fit">
        <Check className="w-2.5 h-2.5" />
        Printclub: {job.message || `${job.included} Flyer versendet an ${job.to}`} · {relTime(job.completed_at)}
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span data-testid="printclub-status-pill" className="inline-flex items-start gap-1 text-[10px] font-mono text-red-300 border border-red-500/40 bg-red-500/10 rounded px-1.5 py-0.5 w-fit max-w-full">
        <X className="w-2.5 h-2.5 mt-0.5 shrink-0" />
        <span className="break-words">Printclub fehlgeschlagen ({relTime(job.completed_at)}): {job.message}</span>
      </span>
    );
  }
  return null;
}

const ENV_PAPER = { standard: 'C4-Umschläge', breakup: 'Break-up-Umschläge (DL, 105 × 210 mm)' };
const ENV_LABEL = { standard: 'Standard-Umschläge', breakup: 'Break-up-Umschläge' };
const BLOCK_SIZE = 50;  // postal batches (Talu, 10.10.2026)

const summarizeBatch = (b, breakupIds) => {
  const now = Date.now();
  const isWriting = j => j.status === 'printed' && j.est_done_at && new Date(j.est_done_at).getTime() > now;
  const starts = b.map(j => (j.claimed_at ? new Date(j.claimed_at).getTime() : null)).filter(Boolean);
  const isBreakup = j => j.letter_kind ? j.letter_kind === 'breakup' : !!breakupIds?.has(j.letter_id);
  const seqs = b.map(j => j.batch_seq || 0).filter(Boolean);
  return {
    id: b[0].batch_id, total: b.length, device: b[0].device_name || '', deviceId: b[0].device_id || '',
    kind: b.every(isBreakup) ? 'breakup' : b.some(isBreakup) ? 'mixed' : 'standard',
    seqFrom: seqs.length ? Math.min(...seqs) : 0, seqTo: seqs.length ? Math.max(...seqs) : 0,
    recipients: new Set(b.map(j => j.letter_id || j.id)).size,
    minStart: starts.length ? Math.min(...starts) : null,
    done: b.filter(j => j.status === 'printed' && !isWriting(j)).length,
    writing: b.filter(isWriting).length,
    hold: b.filter(j => j.status === 'hold').length,
    letters: b.filter(j => (j.doc_type || 'letter') === 'letter' && j.status === 'hold').length,
    dateOverride: (b.find(j => (j.doc_type || 'letter') === 'letter' && j.status === 'hold' && j.date_override) || {}).date_override || '',
    running: b.filter(j => j.status === 'queued' || j.status === 'sent').length,
    errors: b.filter(j => j.status === 'error').length,
  };
};

// Every series (batch) of the given doc types that still has open work — one machine per series, several in parallel.
const batchesOf = (jobs, docTypes, breakupIds) => {
  const groups = {};
  jobs.filter(j => j.batch_id && docTypes.includes(j.doc_type || 'letter')).forEach(j => { (groups[j.batch_id] = groups[j.batch_id] || []).push(j); });
  return Object.values(groups).map(b => summarizeBatch(b, breakupIds)).filter(b => b.hold > 0 || b.running > 0 || b.writing > 0);
};

function DevicePicker({ devices, value, onChange, testId }) {
  if (!devices || devices.length < 2) return null;
  return (
    <label className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground" data-testid={testId}>
      <Printer className="w-3 h-3" /> Schreiben auf
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={`${testId}-select`}
        className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-border px-1.5 text-foreground focus:outline-none">
        {devices.map(d => <option key={d.id} value={d.id}>{d.name}{d.online ? ' • online' : ' • offline'}{d.queued ? ` (${d.queued} in Queue)` : ''}</option>)}
      </select>
    </label>
  );
}

// "Post" page (Talu, 18.10.2026): every team member ticks off what actually went to the post office.
export function MarkSentPanel({ onDone }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    try { return window.localStorage.getItem('markSent.sort') || 'recipient'; } catch { return 'recipient'; }
  });
  const changeSort = (v) => { setSortBy(v); try { window.localStorage.setItem('markSent.sort', v); } catch { /* ignore */ } };

  const load = () => {
    setRows(null); setSel({});
    api.getSentCandidates().then(r => {
      const cands = r.data.candidates || [];
      setRows(cands);
      setSel(Object.fromEntries(cands.filter(c => c.letter_done && c.flyer_done && c.envelope_done)
        .map(c => [c.letter_id, true])));
    }).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  // On the stuffed envelopes only the recipient is visible → default order = owner's last name.
  const coll = new Intl.Collator('de', { sensitivity: 'base' });
  const recipientKey = (f) => `${f.owner_last_name || f.recipient_name || 'zzz'} ${f.owner_first_name || ''}`.trim();
  const sortedRows = (rows || []).slice().sort((a, b) => sortBy === 'recipient'
    ? coll.compare(recipientKey(a), recipientKey(b)) || coll.compare(a.company_name, b.company_name)
    : coll.compare(a.company_name, b.company_name));
  const recipientLabel = (f) => f.owner_last_name
    ? `${f.owner_last_name}${f.owner_first_name ? `, ${f.owner_first_name}` : ''}`
    : (f.recipient_name || '');

  const ids = Object.keys(sel).filter(k => sel[k]);
  const allSelected = rows?.length > 0 && ids.length === rows.length;
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries((rows || []).map(f => [f.letter_id, true])));
  const complete = (rows || []).filter(c => c.letter_done && c.flyer_done && c.envelope_done).length;

  const submit = async () => {
    if (!ids.length) return;
    const nBu = ids.filter(i => rows?.find(r => r.letter_id === i)?.breakup).length;
    const nStd = ids.length - nBu;
    const parts = [`${ids.length} Briefe als versendet markieren?`];
    if (nStd) parts.push(`${nStd} Erstansprache-Brief${nStd === 1 ? '' : 'e'}: startet das 45-Tage-Antwortfenster, verschiebt die Deals nach "Contacted" und schreibt die Owner automatisch in die Follow-up-Sequenz ein (Follow-ups zählen ab Zustellung, +3 Tage).`);
    if (nBu) parts.push(`${nBu} Break-up-Brief${nBu === 1 ? '' : 'e'}: anderer Rhythmus — der Sequenz-Schritt wird abgeschlossen; kommt innerhalb von 10 Tagen keine Reaktion, wird der Deal automatisch gepasst.`);
    if (!window.confirm(parts.join('\n\n'))) return;
    setBusy(true);
    try {
      const r = await api.markAllPrintSent(ids);
      toast.success(`${r.data.marked} Briefe als versendet markiert${r.data.sequences ? ` — ${r.data.sequences} Follow-up-Sequenzen gestartet` : ''}${r.data.deduped ? ` · ${r.data.deduped} Duplikat${r.data.deduped === 1 ? '' : 'e'} aus der Queue entfernt` : ''}`, { duration: 8000 });
      const skipped = r.data.skipped || [];
      if (skipped.length) toast.warning(`${skipped.length} übersprungen — z.B. ${skipped[0].company_name}: ${skipped[0].reason}`, { duration: 8000 });
      load();
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const missingOf = (f) => {
    const m = [];
    if (!f.letter_done) m.push('Brief');
    if (!f.flyer_done) m.push('QR-Flyer (abbuchen)');
    if (!f.envelope_done) m.push('Umschlag');
    return m;
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="mark-sent-panel">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
        <Send className="w-4 h-4 text-emerald-400" />
        <h2 className="font-heading text-base text-foreground">Briefe als versendet markieren</h2>
        {rows !== null && (
          <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5" data-testid="mark-sent-counts">
            {complete} komplett · {rows.length} in Produktion
          </span>
        )}
        <button onClick={load} className="ml-auto text-[10px] text-muted-foreground hover:text-foreground" data-testid="mark-sent-reload">aktualisieren</button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Komplett produzierte Sendungen (Brief + Vorrats-Flyer mit QR abgebucht + Umschlag; Break-ups: Brief + Umschlag) sind vorausgewählt. Markiere nur, was wirklich zur Post geht — der Rest bleibt in der Produktion.
          Break-up-Briefe folgen einem eigenen Rhythmus: 10 Tage ohne Reaktion → Deal wird automatisch gepasst.
        </p>
        {rows === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4" data-testid="mark-sent-empty">Keine freigegebenen Briefe in der Produktion.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-border pb-1.5 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="mark-sent-select-all"
                  className="accent-emerald-500" />
                Alle auswählen ({rows.length})
              </label>
              <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
                Sortierung:
                <button onClick={() => changeSort('recipient')} data-testid="mark-sent-sort-recipient"
                  className={`px-1.5 py-0.5 rounded-sm border transition-colors ${sortBy === 'recipient' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border hover:text-foreground'}`}>
                  Empfänger (Nachname)
                </button>
                <button onClick={() => changeSort('company')} data-testid="mark-sent-sort-company"
                  className={`px-1.5 py-0.5 rounded-sm border transition-colors ${sortBy === 'company' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border hover:text-foreground'}`}>
                  Firma
                </button>
              </span>
            </div>
            <div className="space-y-0.5 max-h-[60vh] overflow-y-auto pr-1">
              {sortedRows.map((f, i) => {
                const missing = missingOf(f);
                const primary = sortBy === 'recipient' ? (recipientLabel(f) || f.company_name) : f.company_name;
                const secondary = sortBy === 'recipient' ? (recipientLabel(f) ? f.company_name : '') : (f.recipient_name || '');
                return (
                  <label key={f.letter_id} data-testid={`mark-sent-cand-${i}`}
                    className="flex items-center gap-2 text-xs cursor-pointer px-1 py-1 rounded-sm hover:bg-secondary/50">
                    <input type="checkbox" checked={!!sel[f.letter_id]} className="accent-emerald-500"
                      onChange={e => setSel(s => ({ ...s, [f.letter_id]: e.target.checked }))} />
                    <span className="text-foreground truncate" data-testid={`mark-sent-cand-${i}-primary`}>{primary}</span>
                    <PrioBadge prio={f.prio} testid={`mark-sent-cand-${i}-prio`} />
                    {secondary && (
                      <span className="text-muted-foreground truncate">· {secondary}</span>
                    )}
                    {f.breakup && <span className="shrink-0 text-[9px] font-mono border rounded px-1 py-px border-amber-500/40 text-amber-300">Break-up</span>}
                    <span className={`ml-auto shrink-0 text-[9px] font-mono border rounded px-1.5 py-0.5 ${missing.length === 0
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                      : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}>
                      {missing.length === 0 ? '✓ komplett' : `offen: ${missing.join(', ')}`}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 pt-1 border-t border-border">
          <Button size="sm" onClick={submit} disabled={busy || !ids.length} data-testid="mark-sent-submit"
            className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-7 px-3">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            {ids.length ? `${ids.length} als versendet markieren` : 'Als versendet markieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EnvelopeSelectDialog({ open, onOpenChange, onDone, devices = [], defaultDevice = '', kind = 'standard' }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const isBreakup = kind === 'breakup';

  useEffect(() => {
    if (!open) return;
    setRows(null); setSel({}); setDeviceId(defaultDevice);
    api.getEnvelopeCandidates().then(r => {
      const cands = (r.data.candidates || []).filter(c => !!c.breakup === isBreakup);
      setRows(cands);
      setSel(Object.fromEntries(cands.filter(c => c.flyer_done).map(c => [c.letter_id, true])));
    }).catch(() => setRows([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = Object.keys(sel).filter(k => sel[k]);
  const allSelected = rows?.length > 0 && ids.length === rows.length;
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries((rows || []).map(f => [f.letter_id, true])));

  const submit = async () => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.createPrintBatch('envelope', ids, deviceId, kind);
      toast.success(`${r.data.count} ${isBreakup ? 'Break-up-' : ''}Umschläge vorbereitet (alphabetische Reihenfolge${r.data.device_name ? ` · ${r.data.device_name}` : ''}) — jetzt ${ENV_PAPER[kind]} einlegen und starten`, { duration: 8000 });
      const skipped = r.data.skipped || [];
      if (skipped.length) toast.warning(`${skipped.length} übersprungen — z.B. ${skipped[0].company_name}: ${skipped[0].reason}`, { duration: 8000 });
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="env-select-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-purple-400" /> {isBreakup ? 'Break-up-Umschläge' : 'Standard-Umschläge'} für Auswahl schreiben
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">
          {isBreakup
            ? `Nur Break-up-Briefe (kein Flyer) — Papier: ${ENV_PAPER.breakup}.`
            : 'Briefe mit fertigem Flyer sind vorausgewählt — offene/kaputte Flyer bleiben in der Queue und können später nachgeholt werden. Tipp: Kaputte Flyer zuerst über „Flyer nachdrucken" zurücksetzen, dann stimmt die Vorauswahl hier automatisch.'}
        </p>
        {rows === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4">Keine offenen Umschläge — alle geschrieben oder bereits in der Queue.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer border-b border-border pb-1.5">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="env-select-all"
                className="accent-purple-500" />
              Alle auswählen ({rows.length})
            </label>
            <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
              {rows.map((f, i) => (
                <label key={f.letter_id} data-testid={`env-cand-${i}`}
                  className="flex items-center gap-2 text-xs cursor-pointer px-1 py-1 rounded-sm hover:bg-secondary/50">
                  <input type="checkbox" checked={!!sel[f.letter_id]} className="accent-purple-500"
                    onChange={e => setSel(s => ({ ...s, [f.letter_id]: e.target.checked }))} />
                  <span className="text-foreground truncate">{f.company_name}</span>
                  <PrioBadge prio={f.prio} testid={`env-cand-prio-${i}`} />
                  {f.recipient_name && (
                    <span className="text-muted-foreground truncate" data-testid={`env-cand-gf-${i}`}>· {f.recipient_name}</span>
                  )}
                  <span className={`ml-auto shrink-0 text-[9px] font-mono border rounded px-1.5 py-0.5 ${f.breakup ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : f.flyer_done
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}>
                    {f.breakup ? 'Break-up' : f.flyer_done ? '✓ Flyer geschrieben' : 'Flyer offen'}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-border flex-wrap">
          <DevicePicker devices={devices} value={deviceId} onChange={setDeviceId} testId="env-select-device" />
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
              className="text-muted-foreground rounded-sm text-xs h-7 px-3">Abbrechen</Button>
            <Button size="sm" onClick={submit} disabled={busy || !ids.length} data-testid="env-select-submit"
              className="bg-purple-600 hover:bg-purple-500 text-white rounded-sm text-xs h-7 px-3">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
              {ids.length ? `${ids.length} Umschläge vorbereiten` : 'Umschläge vorbereiten'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReprintFlyerDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null); setSel({});
    api.getPrintedFlyers().then(r => setRows(r.data.flyers || [])).catch(() => setRows([]));
  }, [open]);

  const ids = Object.keys(sel).filter(k => sel[k]);
  const allSelected = rows?.length > 0 && ids.length === rows.length;
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries((rows || []).map(f => [f.letter_id, true])));

  const submit = async () => {
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} QR-Flyer erneut drucken?\n\nDie Abbuchung wird zurückgenommen: Die QR-Codes erscheinen wieder im nächsten QR-Paket und werden auf neue Vorrats-Flyer gedruckt & abgebucht.`)) return;
    setBusy(true);
    try {
      const r = await api.reprintFlyers(ids);
      toast.success(`${r.data.flyers} QR-Flyer zurückgesetzt — jetzt neues QR-Paket drucken und die Flyer wieder vom Vorrat abbuchen.`, { duration: 10000 });
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="reprint-flyer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-amber-400" /> QR-Flyer erneut drucken
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Wähle die Flyer, bei denen beim QR-Druck oder Abbuchen etwas schiefging. Sie gelten wieder als offen und kommen ins nächste QR-Paket.
        </p>
        {rows === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4">Keine abgebuchten QR-Flyer in der aktuellen Produktion.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer border-b border-border pb-1.5">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="reprint-select-all"
                className="accent-amber-500" />
              Alle auswählen ({rows.length})
            </label>
            <div className="space-y-0.5 max-h-64 overflow-y-auto pr-1">
              {rows.map((f, i) => (
                <label key={f.letter_id} data-testid={`reprint-flyer-${i}`}
                  className="flex items-center gap-2 text-xs text-foreground/90 cursor-pointer px-1 py-1 rounded-sm hover:bg-secondary/50">
                  <input type="checkbox" checked={!!sel[f.letter_id]} className="accent-amber-500"
                    onChange={e => setSel(s => ({ ...s, [f.letter_id]: e.target.checked }))} />
                  <span className="truncate">{f.company_name}</span>
                  <PrioBadge prio={f.prio} testid={`reprint-flyer-prio-${i}`} />
                </label>
              ))}
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 pt-1 border-t border-border">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
            className="text-muted-foreground rounded-sm text-xs h-7 px-3">Abbrechen</Button>
          <Button size="sm" onClick={submit} disabled={busy || !ids.length} data-testid="reprint-submit-btn"
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-7 px-3">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
            {ids.length ? `${ids.length} QR-Flyer erneut drucken` : 'QR-Flyer erneut drucken'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QrOverlayDialog({ open, onOpenChange, openFlyers, onChanged }) {
  const [dx, setDx] = useState('0');
  const [dy, setDy] = useState('0');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open) return;
    api.printSettings().then(r => {
      setDx(String(r.data.qr_overlay_dx_mm ?? 0));
      setDy(String(r.data.qr_overlay_dy_mm ?? 0));
    }).catch(() => {});
  }, [open]);

  const saveOffsets = async () => {
    const sx = String(dx).trim().replace(',', '.');
    const sy = String(dy).trim().replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(sx) || !/^-?\d+(\.\d+)?$/.test(sy)) { toast.error('Versatz muss eine Zahl in mm sein'); return false; }
    const x = parseFloat(sx);
    const y = parseFloat(sy);
    try { await api.updatePrintSettings({ qr_overlay_dx_mm: x, qr_overlay_dy_mm: y }); return true; }
    catch (err) { toast.error(err.response?.data?.detail || 'Versatz speichern fehlgeschlagen'); return false; }
  };

  const download = async (key, fn, filename, okMsg) => {
    setBusy(key);
    try {
      if (key !== 'master' && !(await saveOffsets())) return;
      const res = await fn();
      const n = res.headers?.['x-flyer-count'] || '';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename(n); a.click();
      URL.revokeObjectURL(url);
      toast.success(okMsg(n), { duration: 10000 });
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text())?.detail; } catch { detail = ''; }
      }
      toast.error(detail || 'Download fehlgeschlagen', { duration: 10000 });
    } finally { setBusy(''); }
  };

  const stepCls = 'border border-border rounded-sm p-2.5 space-y-1.5';
  const numCls = 'w-4 h-4 rounded-full border border-purple-400/60 text-purple-300 flex items-center justify-center text-[9px] font-mono shrink-0';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="qr-overlay-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <Printer className="w-4 h-4 text-sky-400" /> QR-Codes auf Vorrats-Flyer drucken
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Die iAuto schreibt Vorrats-Flyer ohne QR-Code. Die QR-Codes der aktuellen Briefe laden Sie hier
            in Paketen à 50 (Namensliste sichtbar), drucken sie mit dem normalen Drucker in das weiße Feld
            und buchen die Flyer danach vom Vorrat ab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className={stepCls}>
            <div className="flex items-center gap-1.5">
              <span className={numCls}>1</span>
              <span className="text-xs font-medium text-foreground">Blanko-Master bei Printclub bestellen</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Flyer-Design ohne QR-Code und ohne Grußtext (2 Seiten: Vorder- + Rückseite). Bestellung
              per E-Mail über „Blanko-Vordrucke bei Printclub nachbestellen…" im Schritt Briefe —
              oder Master hier laden und selbst bestellen. Den (immer gleichen) Flyer-Text schreibt die iAuto
              danach als Vorrat in 50er-Paketen.
            </p>
            <button onClick={() => download('master', api.downloadBlankFlyerPdf,
              () => 'OLTA_Flyer_Blanko_Master.pdf',
              () => 'Blanko-Master geladen — bei Printclub beidseitig (Duplex, lange Kante) drucken lassen')}
              disabled={busy === 'master'} data-testid="qr-overlay-master-btn"
              className="flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-200 disabled:opacity-50 transition-colors">
              {busy === 'master' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Blanko-Master laden (PDF, ohne QR)
            </button>
          </div>
          <div className={stepCls}>
            <div className="flex items-center gap-1.5">
              <span className={numCls}>2</span>
              <span className="text-xs font-medium text-foreground">Drucker kalibrieren</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Testseite auf EINEN Blanko-Flyer drucken: Der Rahmen muss das weiße QR-Feld exakt
              umschließen. Sitzt er daneben, Versatz anpassen (positiv = nach rechts/unten) und erneut testen.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                X
                <input value={dx} onChange={e => setDx(e.target.value)} data-testid="qr-offset-x"
                  className="w-14 h-6 px-1.5 text-[11px] font-mono bg-secondary border border-border rounded-sm text-foreground" />
                mm
              </label>
              <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                Y
                <input value={dy} onChange={e => setDy(e.target.value)} data-testid="qr-offset-y"
                  className="w-14 h-6 px-1.5 text-[11px] font-mono bg-secondary border border-border rounded-sm text-foreground" />
                mm
              </label>
              <button onClick={() => download('test', api.downloadQrOverlayTestPdf,
                () => 'OLTA_QR_Kalibrierung.pdf',
                () => 'Testseite geladen — auf einen Blanko-Flyer drucken und Rahmen prüfen')}
                disabled={busy === 'test'} data-testid="qr-overlay-test-btn"
                className="flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-50 transition-colors">
                {busy === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
                Testseite laden
              </button>
            </div>
          </div>
          <div className={stepCls}>
            <div className="flex items-center gap-1.5">
              <span className={numCls}>3</span>
              <span className="text-xs font-medium text-foreground">QR-Codes in 50er-Paketen drucken & Flyer abbuchen</span>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto" data-testid="qr-overlay-open-count">{openFlyers} offen</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Paket bilden → Namensliste prüfen (Reihenfolge = Seitenreihenfolge der PDF) → PDF einseitig auf
              genauso viele Vorrats-Flyer drucken, Stapel nicht mischen → „abbuchen“. Jedes Paket bleibt mit seinen
              Namen sichtbar, bis es abgebucht ist.
            </p>
            <FlyerQrPackages open={open} beforeDownload={saveOffsets} onChanged={onChanged} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrderBlanksDialog({ open, onOpenChange, onOrdered }) {
  const [qty, setQty] = useState('1000');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = parseInt(String(qty).replace(/\D/g, ''), 10);
    if (!n || n < 1) { toast.error('Bitte eine gültige Stückzahl angeben'); return; }
    setBusy(true);
    try {
      const r = await api.sendFlyerToPrintclub(n);
      toast.success(`Bestellung über ${n} Blanko-Flyer wird an ${r.data.to} gesendet — Status siehe Produktions-Schritt`, { duration: 8000 });
      onOpenChange(false);
      onOrdered?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="order-blanks-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-400" /> Blanko-Vordrucke bei Printclub bestellen
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            E-Mail an den Printclub mit dem Blanko-Master (2 Seiten: Vorder- + Rückseite, ohne QR-Code,
            ohne Grußtext) und der gewünschten Stückzahl. QR-Codes + Text kommen später lokal drauf
            (QR-Overlay + iAuto).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-xs text-foreground">
            Stückzahl
            <input value={qty} onChange={e => setQty(e.target.value)} data-testid="order-blanks-qty"
              className="w-24 h-7 px-2 text-sm font-mono bg-secondary border border-border rounded-sm text-foreground" />
          </label>
          <p className="text-[10px] text-muted-foreground">
            Druckanweisung in der E-Mail: A4, 120gr Mondi Color Copy, beidseitig (Duplex, lange Kante),
            alle Exemplare identisch — der weiße QR-Kasten bleibt frei.
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}
              className="h-7 text-[11px] rounded-sm">Abbrechen</Button>
            <Button size="sm" onClick={submit} disabled={busy} data-testid="order-blanks-send-btn"
              className="h-7 text-[11px] rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Bestellung senden
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const MailingBatchPanel = ({ jobs, onChanged, onMarkedSent, devices = [], activeDeviceId = '' }) => {
  const [mailing, setMailing] = useState(null);
  const [deviceId, setDeviceId] = useState('');
  const [bkDeviceId, setBkDeviceId] = useState('');  // break-up letters: own paper → own iAuto (Talu, 10.10.2026)
  const [envDeviceId, setEnvDeviceId] = useState('');
  const [stockDeviceId, setStockDeviceId] = useState('');
  const [busy, setBusy] = useState('');
  const [checkBusy, setCheckBusy] = useState('');
  const [reprintOpen, setReprintOpen] = useState(false);
  const [envSelectOpen, setEnvSelectOpen] = useState(''); // '' | 'standard' | 'breakup'
  const [qrOverlayOpen, setQrOverlayOpen] = useState(false);
  const [orderBlanksOpen, setOrderBlanksOpen] = useState(false);
  const [earliestOnly, setEarliestOnly] = useState(true); // postal batches of 50: prepare the 50 earliest by sending date first
  const [previewLetter, setPreviewLetter] = useState(null);
  const [pcJob, setPcJob] = useState(null); // latest Printclub job (for status pill)

  const loadMailing = () => api.printMailingStatus().then(r => setMailing(r.data)).catch(() => {});
  useEffect(() => { loadMailing(); }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the latest Printclub job on mount and poll every 3 s while it's still running.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.printclubStatus();
        const latest = (r.data.jobs || [])[0] || null;
        if (!cancelled) {
          const prev = pcJob;
          setPcJob(latest);
          if (latest && prev && prev.status === 'running' && latest.status !== 'running') {
            if (latest.status === 'sent') {
              toast.success(`Printclub: ${latest.included} Flyer an ${latest.to} versendet`, { duration: 8000 });
            } else if (latest.status === 'failed') {
              toast.error(`Printclub-Versand fehlgeschlagen: ${latest.message || 'unbekannt'}`, { duration: 12000 });
            }
          }
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [pcJob?.id, pcJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (okMsg) toast.success(okMsg(r));
      const skipped = r?.data?.skipped || [];
      if (skipped.length) {
        toast.warning(`${skipped.length} übersprungen — z.B. ${skipped[0].company_name}: ${skipped[0].reason}`, { duration: 8000 });
      }
      onChanged();
      return true;
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); return false; }
    finally { setBusy(''); }
  };

  const toggleCheck = async (row) => {
    setCheckBusy(row.id);
    try {
      await api.setPairCheck(row.id, !row.checked);
      setMailing(m => ({
        ...m,
        pair_unchecked: (m.pair_unchecked || 0) + (row.checked ? 1 : -1),
        letters: (m.letters || []).map(l => (l.id === row.id ? { ...l, checked: !row.checked } : l)),
      }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setCheckBusy(''); }
  };

  if (!mailing) return null;
  const open = mailing.open_counts || {};
  const rewriteFlyers = mailing.rewrite_flyers || 0;
  const openFlyerTotal = (open.flyer || 0) + rewriteFlyers;
  // Flyers are pre-written stock (Talu, 10.10.2026: no more "pairs") — step 1 is letters only:
  // first-contact + break-up letters that still have to be written.
  const openLettersAll = (mailing.letters || []).filter(l => !l.letter_done);
  // free for a new series (not in a running/prepared one) — in sending order (Talu, 14.10.2026): earliest date first,
  // break-ups by posting deadline, first letters by approval date; alphabetical within a day
  const openFree = openLettersAll.filter(l => !l.queued)
    .sort((a, b) => (a.send_by || '9').localeCompare(b.send_by || '9') || (a.company_name || '').localeCompare(b.company_name || ''));
  const limitActive = earliestOnly && openFree.length > POSTAL_BATCH;
  const openLetters = limitActive ? openFree.slice(0, POSTAL_BATCH) : openFree;
  const openLettersLater = limitActive ? openFree.slice(POSTAL_BATCH) : [];
  const inSeries = openLettersAll.length - openFree.length;
  const unchecked = openLetters.filter(l => !l.checked).length;
  const allChecked = openLetters.length > 0 && unchecked === 0;
  const openStd = openLetters.filter(l => !l.no_flyer);
  const openBk = openLetters.filter(l => l.no_flyer);
  const hasBothKinds = openStd.length > 0 && openBk.length > 0;
  const missingLanding = openLetters.filter(l => !l.has_flyer && !l.no_flyer);
  const lettersDone = !open.letter;
  const checks = mailing.checks || {};
  const previewChecked = (l) => !!checks[l.id]?.all;
  const uncheckedPreview = openLetters.filter(l => !previewChecked(l));
  // Guided check run over every free open letter: letter → envelope → next recipient.
  const startCheckRun = () => {
    const first = uncheckedPreview[0] || openLetters[0];
    if (first) setPreviewLetter({ id: first.id, company_name: first.company_name, include_headline: first.include_headline, letter_kind: first.no_flyer ? 'breakup' : '', prio: first.prio });
  };
  const goNextRecipient = () => {
    if (!previewLetter || !openLetters.length) { setPreviewLetter(null); return; }
    const idx = openLetters.findIndex(l => l.id === previewLetter.id);
    for (let i = 1; i <= openLetters.length; i++) {
      const cand = openLetters[(Math.max(idx, 0) + i) % openLetters.length];
      if (cand.id !== previewLetter.id && !previewChecked(cand)) {
        setPreviewLetter({ id: cand.id, company_name: cand.company_name, include_headline: cand.include_headline, letter_kind: cand.no_flyer ? 'breakup' : '', prio: cand.prio });
        return;
      }
    }
    setPreviewLetter(null);
    toast.success('Alle offenen Briefe geprüft 🎉');
  };
  const checkProgress = {
    index: Math.max(1, openLetters.findIndex(l => l.id === previewLetter?.id) + 1),
    total: openLetters.length, checked: openLetters.length - uncheckedPreview.length,
  };
  const envDone = !open.envelope;
  const doneSteps = (lettersDone ? 1 : 0) + (envDone ? 1 : 0);
  const allDone = lettersDone && envDone;
  const bookedFlyers = (mailing.letters || []).filter(l => !l.no_flyer && l.flyer_done).length;

  const breakupIds = new Set((mailing.letters || []).filter(l => l.no_flyer).map(l => l.id));
  const letterJobs = jobs.filter(j => !j.stock);
  const pairBatches = batchesOf(letterJobs, ['letter', 'flyer'], breakupIds);
  const envBatches = batchesOf(letterJobs, ['envelope'], breakupIds);
  const stockBatches = batchesOf(jobs.filter(j => j.stock), ['flyer'], breakupIds);
  // Target machine for the next series: the chosen one, else a device not busy with a running series, else the active one.
  const busyDeviceIds = new Set([...pairBatches, ...envBatches, ...stockBatches].filter(b => b.running > 0 || b.writing > 0).map(b => b.deviceId));
  const freeDevice = devices.find(d => !busyDeviceIds.has(d.id) && d.online) || devices.find(d => !busyDeviceIds.has(d.id));
  const pickTarget = (chosen) => (chosen && devices.some(d => d.id === chosen)) ? chosen : (freeDevice?.id || activeDeviceId || '');
  const nameOf = (id) => devices.find(d => d.id === id)?.name || '';
  const targetDevice = pickTarget(deviceId);
  const targetName = nameOf(targetDevice);
  // Break-ups default to the OTHER iAuto so both paper stacks write in parallel.
  const otherDevice = devices.find(d => d.id !== targetDevice && d.online) || devices.find(d => d.id !== targetDevice);
  const bkTargetDevice = (bkDeviceId && devices.some(d => d.id === bkDeviceId)) ? bkDeviceId : (otherDevice?.id || targetDevice);
  const bkTargetName = nameOf(bkTargetDevice);
  const kindLabel = (b, plural = 'Briefe') => b.kind === 'breakup' ? `Break-up-${plural}` : b.kind === 'mixed' ? `${plural} (Erstansprache + Break-up gemischt)` : `Erstansprache-${plural}`;
  const envTargetDevice = pickTarget(envDeviceId);
  const envTargetName = nameOf(envTargetDevice);
  const stockTargetDevice = pickTarget(stockDeviceId);
  const isActive = (b) => b.running > 0 || b.writing > 0;

  const renderProgress = (batch, key, label) => (
    <div className="space-y-1" data-testid={`mailing-running-${key}`} key={batch.id}>
      <div className="flex items-center gap-1.5">
        {devices.length > 1 && batch.device && (
          <span className="text-[9px] font-mono px-1.5 py-px rounded border text-purple-300 border-purple-500/40 bg-purple-500/10 shrink-0" data-testid={`mailing-running-${key}-device`}>
            <Printer className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />{batch.device}
          </span>
        )}
        <div className="h-1.5 flex-1 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-purple-500 transition-all"
            style={{ width: `${Math.round((batch.done / batch.total) * 100)}%` }} />
        </div>
        <button onClick={() => {
          if (!window.confirm(`Verbleibende ${batch.running + batch.hold} offene ${label}-Jobs dieser Serie stornieren (wartende UND bereits an den Agent übertragene)?\n\nBereits geschriebene bleiben erhalten; schreibt die Maschine gerade, läuft das am Gerät weiter.`)) return;
          run(`cancel-${batch.id}`, () => api.deletePrintBatch(batch.id, true), r => `${r.data.deleted} Jobs storniert`);
        }} disabled={busy === `cancel-${batch.id}`} data-testid={`mailing-cancel-running-${key}`}
          className="p-0.5 text-muted-foreground hover:text-red-400 transition-colors shrink-0" title="Serie abbrechen (wartende und hängende übertragene Jobs stornieren)">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground font-mono">
        {label}{batch.seqFrom ? ` #${batch.seqFrom}–${batch.seqTo}` : ''} · {batch.done}/{batch.total} geschrieben
        {batch.writing ? ` · ${batch.writing} schreibt gerade` : ''} · {batch.running} warten
        {batch.errors ? ` · ${batch.errors} Fehler` : ''}
        {batch.minStart ? ` · läuft seit ${fmtDur((Date.now() - batch.minStart) / 1000)}` : ''}
      </p>
    </div>
  );

  // A prepared (HOLD) series waiting for its "Jetzt schreiben" — one row per series with its target machine.
  const renderPrepared = (b, key, label) => (
    <div key={b.id} className="flex items-center gap-1.5 flex-wrap" data-testid={`mailing-prepared-${key}`}>
      {devices.length > 1 && (
        <label className="inline-flex items-center gap-1 text-[9px] font-mono px-1 py-px rounded border text-purple-300 border-purple-500/40 bg-purple-500/10"
          data-testid={`mailing-prepared-${key}-device`} title="iAuto für diese vorbereitete Serie wählen — verschiebt alle wartenden Jobs der Serie">
          <Printer className="w-2.5 h-2.5 shrink-0" />
          <select value={b.deviceId || ''} disabled={busy === `dev-${b.id}`}
            onChange={e => run(`dev-${b.id}`, () => api.reassignPrintBatchDevice(b.id, e.target.value), r => `Serie → ${r.data.device_name}`)}
            data-testid={`mailing-prepared-${key}-device-select`}
            className="bg-transparent text-purple-200 focus:outline-none cursor-pointer max-w-[120px]">
            {devices.map(d => <option key={d.id} value={d.id} className="bg-card text-foreground">{d.name}{d.online ? ' • online' : ' • offline'}</option>)}
          </select>
        </label>
      )}
      <Button size="sm" onClick={() => run(`start-${b.id}`, () => api.startPrintBatch(b.id))}
        disabled={busy === `start-${b.id}`} data-testid={`mailing-start-${key}`}
        className="h-6 text-[10px] rounded-sm bg-purple-600 hover:bg-purple-500 text-white">
        {busy === `start-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
        Jetzt schreiben ({b.hold} {label}{b.seqFrom ? ` #${b.seqFrom}–${b.seqTo}` : ''})
      </Button>
      {b.recipients > BLOCK_SIZE && (
        <Button size="sm" variant="outline" disabled={busy === `blocks-${b.id}`} data-testid={`mailing-split-blocks-${key}`}
          title={`Post-Chargen à ${BLOCK_SIZE}: Serie in ${Math.ceil(b.recipients / BLOCK_SIZE)} Blöcke teilen (#1–${BLOCK_SIZE}, #${BLOCK_SIZE + 1}–${2 * BLOCK_SIZE} …) — jeder Block startet einzeln mit eigenem Datum, die alphabetische Nummerierung bleibt (passt zu den vorgedruckten QR-Flyern)`}
          onClick={() => run(`blocks-${b.id}`, () => api.splitPrintBatchBlocks(b.id, BLOCK_SIZE),
            r => `${r.data.blocks.length} Blöcke: ${r.data.blocks.map(x => `#${x.from}–${x.to} (${x.count})`).join(' · ')} — Nummerierung wie gedruckt, je Block eigenes Datum + „Jetzt schreiben“`)}
          className="h-6 text-[10px] rounded-sm border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
          {busy === `blocks-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
          In {BLOCK_SIZE}er-Blöcke teilen
        </Button>
      )}
      {b.kind === 'mixed' && b.letters > 0 && (
        <Button size="sm" variant="outline" disabled={busy === `splitkind-${b.id}`} data-testid={`mailing-split-kind-${key}`}
          title="Erstansprache und Break-ups haben unterschiedliches Briefpapier: in zwei Serien trennen (je alphabetisch #1…n) — Break-ups gehen auf die andere iAuto"
          onClick={() => run(`splitkind-${b.id}`, () => api.splitPrintBatchKind(b.id),
            r => `Getrennt: ${r.data.standard.count} Erstansprache → ${r.data.standard.device_name || 'iAuto'} · ${r.data.breakup.count} Break-ups → ${r.data.breakup.device_name || 'iAuto'} — beide Serien alphabetisch neu nummeriert`)}
          className="h-6 text-[10px] rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
          {busy === `splitkind-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
          Erstansprache / Break-ups trennen
        </Button>
      )}
      {b.letters > 0 && (
        <span className="inline-flex items-center gap-1" data-testid={`mailing-batch-date-${key}`}
          title="Brief-Datum für ALLE Briefe dieser Serie setzen — die Briefe werden neu gerendert (leer = heutiges Datum beim Schreiben)">
          <CalendarDays className="w-3 h-3 text-sky-300" />
          <input type="date" value={b.dateOverride || ''} disabled={busy === `date-${b.id}`}
            onChange={e => run(`date-${b.id}`, () => api.setPrintBatchDate(b.id, e.target.value || null),
              r => r.data.date_override ? `Datum ${new Date(r.data.date_override).toLocaleDateString('de-DE')} für ${r.data.letters} Brief${r.data.letters !== 1 ? 'e' : ''} gesetzt — neu gerendert` : `Datum für ${r.data.letters} Briefe zurückgesetzt (heute)`)}
            data-testid={`mailing-batch-date-input-${key}`}
            className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-sky-500/40 px-1 text-foreground focus:outline-none" />
          {b.dateOverride && (
            <button onClick={() => run(`date-${b.id}`, () => api.setPrintBatchDate(b.id, null), r => `Datum für ${r.data.letters} Briefe zurückgesetzt (heute)`)}
              disabled={busy === `date-${b.id}`} data-testid={`mailing-batch-date-reset-${key}`}
              className="text-[10px] px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-foreground" title="Alle Briefe zurück auf das heutige Datum">heute</button>
          )}
        </span>
      )}
      <button onClick={() => run(`cancel-${b.id}`, () => api.deletePrintBatch(b.id))}
        disabled={busy === `cancel-${b.id}`} data-testid={`mailing-cancel-${key}`}
        className="p-1 text-muted-foreground hover:text-red-400 transition-colors" title="Serie verwerfen">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const pairRunningList = pairBatches.filter(isActive).sort((a, b) => a.seqFrom - b.seqFrom);
  const pairPreparedList = pairBatches.filter(b => b.hold > 0 && !isActive(b)).sort((a, b) => (a.kind === 'breakup') - (b.kind === 'breakup') || a.seqFrom - b.seqFrom);
  const pairRunning = pairRunningList.length > 0;
  const pairPrepared = pairPreparedList.length > 0;
  // Two envelope types → two separate series: standard letters (C4) and break-ups (DL). Free = letter written,
  // envelope neither written nor in a running/prepared series.
  const envLetters = (mailing.letters || []).filter(l => !l.envelope_done);
  const envKindState = (kind) => {
    const mine = envLetters.filter(l => !!l.no_flyer === (kind === 'breakup'));
    // 'mixed' series (created before the split) are shown under standard so nothing disappears
    const batches = envBatches.filter(b => kind === 'breakup' ? b.kind === 'breakup' : b.kind !== 'breakup');
    return {
      kind, open: mine.length,
      free: mine.filter(l => l.letter_done && !l.envelope_queued).length,
      inSeries: mine.filter(l => l.envelope_queued).length,
      notReady: mine.filter(l => !l.letter_done).length,
      running: batches.filter(isActive), prepared: batches.filter(b => b.hold > 0 && !isActive(b)).sort((a, b) => a.seqFrom - b.seqFrom),
    };
  };
  const envKinds = [envKindState('standard'), envKindState('breakup')].filter(k => k.open > 0 || k.running.length || k.prepared.length);
  const canSkipToEnv = pcJob?.status === 'sent';
  const envLocked = !lettersDone && !canSkipToEnv;

  const hasPrepared = [...pairBatches, ...envBatches, ...stockBatches].some(b => b.hold > 0 && !isActive(b));

  // Blank-flyer ordering + QR re-prints belong to the flyer stock, not to the letter step.
  const stockExtras = (
    <>
      <button onClick={() => setOrderBlanksOpen(true)} disabled={pcJob?.status === 'running'}
        data-testid="mailing-printclub-btn"
        className="flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-200 disabled:opacity-50 transition-colors">
        {pcJob?.status === 'running' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        Blanko-Vordrucke bei Printclub nachbestellen…
      </button>
      {pcJob && <PrintclubStatusPill job={pcJob} />}
      {bookedFlyers > 0 && (
        <button onClick={() => setReprintOpen(true)} data-testid="mailing-reprint-btn"
          className="flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 transition-colors">
          <RotateCcw className="w-3 h-3" />
          QR-Flyer erneut drucken ({bookedFlyers} abgebucht)…
        </button>
      )}
    </>
  );
  const stockBlock = (
    <FlyerStockBlock stock={mailing.flyer_stock} openQr={openFlyerTotal} devices={devices} deviceId={stockTargetDevice}
      onDeviceChange={setStockDeviceId} onChanged={onChanged} onOpenQr={() => setQrOverlayOpen(true)} DevicePicker={DevicePicker}
      extra={stockExtras}
      renderSeries={<>
        {stockBatches.filter(isActive).map((b, i) => renderProgress(b, i === 0 ? 'stock' : `stock-${i}`, 'Vorrats-Flyer'))}
        {stockBatches.filter(b => b.hold > 0 && !isActive(b)).map((b, i) => renderPrepared(b, i === 0 ? 'stock' : `stock-${i}`, 'Vorrats-Flyer'))}
      </>} />
  );
  const qrDialog = <QrOverlayDialog open={qrOverlayOpen} onOpenChange={setQrOverlayOpen} openFlyers={openFlyerTotal} onChanged={onChanged} />;

  if (!mailing.total) {
    return (
      <div className="px-4 py-3 border-b border-border space-y-2" data-testid="mailing-batch-panel-stock-only">
        <div className="flex items-center gap-2 flex-wrap">
          <Layers className="w-3.5 h-3.5 text-sky-400" />
          <p className="text-xs font-medium text-foreground">Flyer-Vorrat — keine freigegebenen Briefe, die iAuto kann Vorrats-Flyer schreiben</p>
        </div>
        {stockBlock}
        {qrDialog}
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-border space-y-2.5" data-testid="mailing-batch-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Layers className="w-3.5 h-3.5 text-purple-400" />
        <p className="text-xs font-medium text-foreground">Serien-Produktion — {mailing.total} freigegebene Briefe</p>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-purple-300 border-purple-500/40 bg-purple-500/10"
          data-testid="mailing-batch-progress">
          Schritt {Math.min(doneSteps + 1, 2)}/2 · {doneSteps}/2 fertig
        </span>
        <span className="text-[10px] text-muted-foreground ml-auto hidden sm:block">
          Flyer kommen aus dem Vorrat — die iAuto schreibt nur noch die Briefe, die QR-Codes kommen aus dem Drucker
        </span>
      </div>
      {devices.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="mailing-split-row">
          <button onClick={() => run('split-queue', () => api.splitQueueDevices(),
              r => `Vorbereitete Serien auf ${Object.keys(r.data.split || {}).length} iAutos aufgeteilt — ${Object.entries(r.data.split || {}).map(([n, c]) => `${n}: ${c}`).join(', ')}`)}
            disabled={busy === 'split-queue' || !hasPrepared} data-testid="mailing-split-btn"
            title={hasPrepared ? 'Alle vorbereiteten Serien gleichmäßig auf beide iAutos verteilen (alphabetisch; Briefe und Umschläge bleiben getrennt) — danach schreiben beide Maschinen gleichzeitig' : 'Erst eine Serie vorbereiten'}
            className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-sm border border-purple-500/40 text-purple-200 hover:bg-purple-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {busy === 'split-queue' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
            Auf beide iAutos aufteilen
          </button>
          <span className="text-[10px] text-muted-foreground">verteilt die vorbereiteten Serien gleichmäßig, damit beide Maschinen gleichzeitig schreiben</span>
        </div>
      )}
      {stockBlock}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* ── Step 1: Briefe — Erstansprache + Break-up (flyers are pre-written stock, no pairs) ── */}
        <div data-testid="mailing-step-pair"
          className={`border rounded-sm p-2.5 space-y-1.5 ${lettersDone ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-purple-500/50 bg-purple-500/5'}`}>
          <div className="flex items-center gap-1.5">
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-mono border ${lettersDone ? 'border-emerald-400 text-emerald-300' : 'border-border text-muted-foreground'}`}>
              {lettersDone ? <Check className="w-2.5 h-2.5" /> : 1}
            </span>
            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">Briefe</span>
            <span className="text-[10px] text-muted-foreground">Erstansprache + Break-up</span>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto" data-testid="mailing-open-pair">
              {lettersDone ? 'fertig' : `${openLettersAll.length} offen`}
            </span>
          </div>
          {missingLanding.length > 0 && (
            <p className="text-[10px] text-red-300 border border-red-500/30 bg-red-500/10 rounded-sm px-2 py-1"
              data-testid="mailing-missing-flyer-warning">
              ⚠ {missingLanding.length} Brief{missingLanding.length !== 1 ? 'e' : ''} ohne Landing Page / QR-Code
              (z. B. {missingLanding[0].company_name}) — ohne QR kann kein Vorrats-Flyer beiliegen. Erst Landing Page anlegen.
            </p>
          )}
          {pairRunningList.map((b, i) => renderProgress(b, i === 0 ? 'pair' : `pair-${i}`, kindLabel(b, 'Brief')))}
          {pairPreparedList.map((b, i) => renderPrepared(b, i === 0 ? 'pair' : `pair-${i}`, kindLabel(b)))}
          {pairPreparedList.some(b => b.kind === 'mixed') && (
            <p className="text-[10px] text-amber-300/90" data-testid="mailing-mixed-hint">
              Diese Serie mischt Erstansprache- und Break-up-Briefe (zwei Papiersorten). „Erstansprache / Break-ups trennen“ macht daraus
              zwei alphabetische Serien — eine je iAuto, passend zu den zwei Papierstapeln.
            </p>
          )}
          {!lettersDone && openLetters.length > 0 && (
            <>
              {(pairRunning || pairPrepared) && (
                <p className="text-[10px] text-purple-300/90 pt-1" data-testid="mailing-parallel-hint">
                  {openLetters.length} weitere Briefe frei — als zweite Serie{devices.length > 1 ? ' auf einer anderen iAuto parallel' : ''} vorbereiten
                  {inSeries ? ` (${inSeries} stecken schon in einer Serie)` : ''}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap" data-testid="pair-check-run-row">
                <Button size="sm" variant="outline" onClick={startCheckRun} data-testid="pair-check-run-btn"
                  className="h-6 text-[10px] rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                  <ListChecks className="w-3 h-3" />
                  {uncheckedPreview.length
                    ? `${uncheckedPreview.length} offene Briefe prüfen & nachjustieren`
                    : 'Alle geprüft — erneut durchgehen'}
                </Button>
                <span className="text-[10px] text-muted-foreground font-mono" data-testid="pair-check-run-progress">
                  {openLetters.length - uncheckedPreview.length}/{openLetters.length} in der Vorschau geprüft (Brief + Umschlag)
                  {pairRunning ? ' · geht auch, während die Maschine schreibt' : ''}
                </span>
              </div>
              {openFree.length > POSTAL_BATCH && (
                <label className={`inline-flex items-center gap-1.5 text-[10px] cursor-pointer ${earliestOnly ? 'text-sky-300' : 'text-muted-foreground'}`} data-testid="mailing-earliest-toggle">
                  <input type="checkbox" checked={earliestOnly} onChange={e => setEarliestOnly(e.target.checked)} className="accent-sky-500"
                    data-testid="mailing-earliest-checkbox" />
                  <CalendarDays className="w-3 h-3" />
                  Nur die {POSTAL_BATCH} frühesten nach Versanddatum vorbereiten
                  {earliestOnly ? ` — ${openLettersLater.length} spätere bleiben für die nächste Runde` : ` (aus: alle ${openFree.length} auf einmal)`}
                </label>
              )}
              <div className="border border-border/60 rounded-sm max-h-52 overflow-y-auto divide-y divide-border/40"
                data-testid="pair-checklist">
                {openLettersAll.filter(l => l.queued).slice(0, 3).map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2 px-2 py-1 text-xs opacity-60" data-testid={`pair-queued-row-${i}`}>
                    <Loader2 className="w-3 h-3 text-purple-300 shrink-0" />
                    <span className="text-muted-foreground truncate">{l.company_name}</span>
                    <PrioBadge prio={l.prio} testid={`pair-queued-prio-${i}`} />
                    <span className="text-[9px] font-mono text-purple-300/80 shrink-0 ml-auto">{l.queued_hold ? 'vorbereitet' : 'in Serie'}{l.queued_device ? ` · ${l.queued_device}` : ''}</span>
                  </div>
                ))}
                {inSeries > 3 && <div className="px-2 py-1 text-[10px] text-muted-foreground/70 opacity-60">… {inSeries - 3} weitere in laufenden Serien</div>}
                {openLetters.map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2 px-2 py-1 text-xs" data-testid={`pair-row-${i}`}>
                    <input type="checkbox" checked={!!l.checked} disabled={checkBusy === l.id}
                      onChange={() => toggleCheck(l)} data-testid={`pair-check-${i}`}
                      className="accent-purple-500 cursor-pointer" title="Als geprüft abhaken" />
                    <span className="text-foreground truncate">{l.company_name}</span>
                    <PrioBadge prio={l.prio} testid={`pair-prio-${i}`} />
                    {l.recipient_name && <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">· {l.recipient_name}</span>}
                    {l.send_by && (
                      <span className={`text-[9px] font-mono shrink-0 ${l.send_by_kind === 'deadline' ? 'text-amber-300/90' : 'text-muted-foreground'}`} data-testid={`pair-date-${i}`}
                        title={l.send_by_kind === 'deadline' ? 'Break-up: spätestens posten bis' : 'Erstansprache: freigegeben am'}>
                        {l.send_by_kind === 'deadline' ? 'bis ' : 'frei '}{fmtDay(l.send_by)}
                      </span>
                    )}
                    {!l.has_flyer && !l.no_flyer && <span className="text-[9px] font-mono text-red-300 shrink-0">keine Landing Page!</span>}
                    {l.no_flyer
                      ? <span className="text-[9px] font-mono border rounded px-1 py-px border-amber-500/40 text-amber-300 shrink-0" data-testid={`pair-breakup-${i}`}>Break-up</span>
                      : <span className="text-[9px] font-mono text-muted-foreground/70 shrink-0">Erstansprache</span>}
                    <button onClick={() => setPreviewLetter({ id: l.id, company_name: l.company_name, include_headline: l.include_headline, letter_kind: l.no_flyer ? 'breakup' : '', prio: l.prio })}
                      data-testid={`pair-preview-${i}`} title="Brief + Umschlag prüfen & nachjustieren"
                      className="ml-auto p-0.5 text-muted-foreground hover:text-sky-300 transition-colors shrink-0">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {openLettersLater.length > 0 && (
                  <div className="px-2 py-1 text-[10px] text-muted-foreground/70 bg-secondary/20" data-testid="pair-later-divider">
                    ── nächste Runde ({openLettersLater.length} spätere, ab {fmtDay(openLettersLater[0].send_by)}) ──
                  </div>
                )}
                {openLettersLater.slice(0, 3).map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2 px-2 py-1 text-xs opacity-50" data-testid={`pair-later-row-${i}`}>
                    <span className="w-[13px] shrink-0" />
                    <span className="text-muted-foreground truncate">{l.company_name}</span>
                    <PrioBadge prio={l.prio} testid={`pair-later-prio-${i}`} />
                    {l.send_by && <span className="text-[9px] font-mono shrink-0">{l.send_by_kind === 'deadline' ? 'bis ' : 'frei '}{fmtDay(l.send_by)}</span>}
                  </div>
                ))}
                {openLettersLater.length > 3 && <div className="px-2 py-1 text-[10px] text-muted-foreground/60 opacity-60">… {openLettersLater.length - 3} weitere in der nächsten Runde</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" disabled={!allChecked || busy === 'pair' || missingLanding.length > 0}
                  onClick={() => run('pair', () => api.createPrintBatch('pair', openLetters.filter(l => l.checked).map(l => l.id), targetDevice, null,
                      hasBothKinds && devices.length > 1 ? bkTargetDevice : null),
                    r => {
                      const sd = r.data.standard || {}, bd = r.data.breakup || {};
                      const parts = [];
                      if (sd.count) parts.push(`${sd.count} Erstansprache-Briefe${sd.device_name ? ` → ${sd.device_name}` : ''}`);
                      if (bd.count) parts.push(`${bd.count} Break-up-Briefe${bd.device_name ? ` → ${bd.device_name}` : ''}`);
                      return `${parts.join(' + ') || `${r.data.count} Briefe`} vorbereitet (${r.data.jobs} Seiten) — ${parts.length > 1 ? 'zwei getrennte Serien, je alphabetisch #1…n' : 'alphabetische Reihenfolge'}`
                        + (r.data.flyers_from_stock ? ` · ${r.data.flyers_from_stock} Flyer kommen aus dem Vorrat (QR-Pakete drucken & abbuchen)` : '');
                    })}
                  data-testid="mailing-prepare-pair"
                  className="h-6 text-[10px] rounded-sm border-purple-500/40 text-purple-300 hover:bg-purple-500/10 disabled:opacity-50">
                  {busy === 'pair' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                  {allChecked
                    ? (hasBothKinds
                      ? `${openStd.length} Erstansprache + ${openBk.length} Break-ups${limitActive ? ' (die frühesten)' : ''} vorbereiten (2 Serien)`
                      : `${openLetters.length} ${limitActive ? 'früheste' : 'geprüfte'} ${openBk.length ? 'Break-up-Briefe' : 'Briefe'} vorbereiten${devices.length > 1 && targetName ? ` → ${targetName}` : ''}`)
                    : `Erst alle prüfen — ${unchecked} von ${openLetters.length} ungeprüft`}
                </Button>
                <DevicePicker devices={devices} value={targetDevice} onChange={setDeviceId} testId="mailing-pair-device" />
                {hasBothKinds && devices.length > 1 && (
                  <label className="inline-flex items-center gap-1.5 text-[10px] text-amber-300" data-testid="mailing-pair-breakup-device"
                    title="Break-up-Briefe liegen auf eigenem Briefpapier (mit QR) — eigene Serie auf dieser iAuto">
                    <Printer className="w-3 h-3" /> Break-ups auf
                    <select value={bkTargetDevice} onChange={e => setBkDeviceId(e.target.value)} data-testid="mailing-pair-breakup-device-select"
                      className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-amber-500/40 px-1.5 text-foreground focus:outline-none">
                      {devices.map(d => <option key={d.id} value={d.id}>{d.name}{d.online ? ' • online' : ' • offline'}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {hasBothKinds && (
                <p className="text-[10px] text-muted-foreground" data-testid="mailing-two-stacks-hint">
                  Zwei Papierstapel, zwei Serien: Erstansprache (Standard-Briefpapier{devices.length > 1 ? ` → ${targetName}` : ''}) und Break-ups
                  (Break-up-Briefpapier mit QR{devices.length > 1 ? ` → ${bkTargetName}` : ''}) — jede Serie alphabetisch #1…n wie das QR-Overlay.
                </p>
              )}
            </>
          )}
          {!lettersDone && (pairPrepared || pairRunning) && (
            <p className="text-[10px] text-amber-300/90">
              Briefpapier (A4) einlegen — Reihenfolge wie in der Liste (alphabetisch). Flyer kommen aus dem Vorrat (QR-Pakete).
            </p>
          )}
        </div>

        {/* ── Step 2: Umschläge ── */}
        <div data-testid="mailing-step-envelope"
          className={`border rounded-sm p-2.5 space-y-1.5 ${envDone ? 'border-emerald-500/40 bg-emerald-500/5'
            : lettersDone ? 'border-purple-500/50 bg-purple-500/5' : 'border-border opacity-70'}`}>
          <div className="flex items-center gap-1.5">
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-mono border ${envDone ? 'border-emerald-400 text-emerald-300' : 'border-border text-muted-foreground'}`}>
              {envDone ? <Check className="w-2.5 h-2.5" /> : 2}
            </span>
            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">Umschläge</span>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto" data-testid="mailing-open-envelope">
              {envDone ? 'fertig' : `${open.envelope} offen`}
            </span>
          </div>
          {!envDone && envKinds.length > 1 && (
            <p className="text-[10px] text-amber-300/90" data-testid="mailing-env-kinds-hint">
              Zwei Umschlag-Sorten — bitte als getrennte Serien schreiben und jeweils das passende Papier einlegen.
            </p>
          )}
          {envKinds.map((k) => {
            const bk = k.kind === 'breakup';
            const key = bk ? 'envelope-breakup' : 'envelope';
            const idle = !k.running.length && !k.prepared.length;
            return (
              <div key={k.kind} data-testid={`mailing-env-kind-${k.kind}`}
                className={`rounded-sm border p-2 space-y-1.5 ${bk ? 'border-amber-500/30 bg-amber-500/5' : 'border-border/60'}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-medium ${bk ? 'text-amber-300' : 'text-foreground'}`}>{ENV_LABEL[k.kind]}</span>
                  <span className="text-[9px] font-mono text-muted-foreground">{ENV_PAPER[k.kind]}</span>
                  <span className="text-[10px] font-mono text-muted-foreground ml-auto" data-testid={`mailing-env-kind-${k.kind}-open`}>
                    {k.open === 0 ? 'fertig' : `${k.open} offen`}
                  </span>
                </div>
                {k.running.map((b, i) => renderProgress(b, i === 0 ? key : `${key}-${i}`, bk ? 'Break-up-Umschlag' : 'Umschlag'))}
                {k.prepared.map((b, i) => renderPrepared(b, i === 0 ? key : `${key}-${i}`, bk ? 'Break-up-Umschläge' : 'Umschläge'))}
                {k.open > 0 && (k.free > 0 || idle) && (
                  <>
                    {!idle && k.free > 0 && (
                      <p className="text-[10px] text-purple-300/90" data-testid={`mailing-env-kind-${k.kind}-parallel-hint`}>
                        {k.free} weitere frei — als zweite Serie{devices.length > 1 ? ' auf einer anderen iAuto parallel' : ''} vorbereiten
                        {k.inSeries ? ` (${k.inSeries} stecken schon in einer Serie)` : ''}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" disabled={envLocked || k.free === 0 || busy === key}
                        onClick={() => run(key, () => api.createPrintBatch('envelope', null, envTargetDevice, k.kind),
                          r => `${r.data.count} ${bk ? 'Break-up-' : ''}Umschläge vorbereitet (alphabetische Reihenfolge${r.data.device_name ? ` · ${r.data.device_name}` : ''}) — ${ENV_PAPER[k.kind]} einlegen`)}
                        data-testid={`mailing-prepare-${key}`}
                        className={`h-6 text-[10px] rounded-sm disabled:opacity-50 ${bk ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10' : 'border-purple-500/40 text-purple-300 hover:bg-purple-500/10'}`}>
                        {busy === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                        {envLocked ? 'Erst Schritt 1 abschließen'
                          : k.free === 0 ? (k.inSeries ? 'Alle in Serien' : `${k.notReady} Briefe noch nicht geschrieben`)
                          : `${k.free} ${bk ? 'Break-up-' : ''}Umschläge vorbereiten${devices.length > 1 && envTargetName ? ` → ${envTargetName}` : ''}${canSkipToEnv && !lettersDone ? ' (parallel zum Printclub)' : ''}`}
                      </Button>
                      {!envLocked && k.free > 0 && (
                        <DevicePicker devices={devices} value={envTargetDevice} onChange={setEnvDeviceId} testId={`mailing-env-device-${k.kind}`} />
                      )}
                    </div>
                    {envLocked && k.free > 0 && (
                      <button onClick={() => setEnvSelectOpen(k.kind)} data-testid={`env-select-btn-${k.kind}`}
                        className="flex items-center gap-1 text-[10px] text-purple-300 hover:text-purple-200 transition-colors">
                        <ListChecks className="w-3 h-3" />
                        Oder: {ENV_LABEL[k.kind]} nur für fertige Briefe auswählen ({k.free})…
                      </button>
                    )}
                  </>
                )}
                {!idle && (
                  <p className="text-[10px] text-amber-300/90">Papier wechseln: {ENV_PAPER[k.kind]} einlegen</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-2 border border-border rounded-sm p-2.5 text-[11px] text-muted-foreground" data-testid="mailing-post-hint">
        <Send className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        {allDone ? 'Produktion abgeschlossen — Briefe eintüten.' : !open.letter ? 'Alle Briefe geschrieben.' : `${mailing.total - open.letter} von ${mailing.total} Briefen geschrieben.`}{' '}
        Was zur Post geht, wird unter <Link to="/post" className="text-emerald-300 hover:underline" data-testid="mailing-post-link">Post → Als versendet markieren</Link> abgehakt.
      </div>
      <ReprintFlyerDialog open={reprintOpen} onOpenChange={setReprintOpen} onDone={onChanged} />
      {qrDialog}
      <OrderBlanksDialog open={orderBlanksOpen} onOpenChange={setOrderBlanksOpen}
        onOrdered={() => api.printclubStatus().then(res => setPcJob((res.data.jobs || [])[0] || null)).catch(() => {})} />
      <EnvelopeSelectDialog open={!!envSelectOpen} onOpenChange={(o) => setEnvSelectOpen(o ? envSelectOpen : '')} onDone={onChanged}
        devices={devices} defaultDevice={envTargetDevice} kind={envSelectOpen || 'standard'} />
      <PrintPreviewDialog letter={previewLetter} onClose={() => setPreviewLetter(null)}
        onConfirm={null} confirming={false} mailing={mailing?.status} onHeadlineChange={loadMailing}
        checks={checks} onCheckChanged={loadMailing} onNextRecipient={goNextRecipient} progress={checkProgress} />
    </div>
  );
};

export default MailingBatchPanel;
